import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { SEO } from '@/components';
import Calendly from '@/components/Calendly';
import HomeLayout from '@/layouts/home.layout';
import { MAIN_BUTTON } from '@/themes/customizations';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  Modal,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/system';
import Script from 'next/script';
import { IS_DEVELOPMENT, IS_STAGE_SERVER } from '@/common/config';
import { useToggle } from '@/utils/hooks';

declare global {
  const grecaptcha: GrecaptchaEnterprise;
  export interface GrecaptchaEnterprise {
    enterprise: {
      execute: (key: string, options: any) => string;
      ready: (cb: () => void) => void;
      reset: (widget?: number) => void;
    };
    getPageId: () => void;
  }
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'nav',
        'footer',
        'contactus',
        'common',
        'seo'
      ])),
      locale,
      reloadOnPrerender: process.env.NODE_ENV !== 'production'
    }
  };
}

function ContactUs() {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>();
  const { on: loading, toggle: setLoading } = useToggle();
  const { t } = useTranslation();
  const { push, isReady } = useRouter();
  const [openModal, setOpenModal] = useState<string>('');
  const { breakpoints } = useTheme();
  const isTablet = useMediaQuery(breakpoints.down('md'));

  const validateCaptcha = async (): Promise<string | null> =>
    new Promise((resolve) => {
      if (typeof grecaptcha === 'undefined') {
        console.log('No captcha instance found');
        return resolve(null);
      }
      const grecaptchaClientKey = process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY;
      if (!grecaptchaClientKey || !grecaptcha) {
        console.log('Error reading captcha client key');
        return resolve(null);
      }
      grecaptcha?.enterprise?.ready(async function () {
        const token = await grecaptcha.enterprise.execute(grecaptchaClientKey, {
          action: 'LOGIN'
        });
        if (!token) {
          console.log('No captcha token found, refresh & try again');
          return resolve(null);
        }
        try {
          const { data } = await axios({
            method: 'POST',
            url: '/api/captcha/validate',
            baseURL: process.env.NEXT_PUBLIC_DOMAIN,
            data: {
              token
            }
          });
          if (!data?.success) {
            console.log('Captcha invalid, please refresh and try again');
            return resolve(null);
          }
          return resolve(token);
        } catch (e) {
          resolve(null);
        }
      });
    });

  const handleContactForm = async (data) => {
    if (!data) return;
    try {
      setLoading(true);
      const token = await validateCaptcha();
      if (!token) {
        throw new Error(
          'Captcha validation failed, please refresh and try again'
        );
      }
      const baseUrl =
        IS_DEVELOPMENT || IS_STAGE_SERVER
          ? 'https://api.stage.smat.io/api'
          : 'https://api.beta.smat.io/api';

      const {
        terms: agreeCommunications,
        listing: listingAndCommercializing,
        financing: findingFinancing,
        ...dataToSend
      } = data;
      axios({
        url: `${baseUrl}/contactus`,
        data: {
          agreeCommunications,
          listingAndCommercializing,
          findingFinancing,
          ...dataToSend
        },
        method: 'POST' as const
      })
        .then(({ data }) => {
          axios({
            method: 'POST',
            url: '/api/crm/contact',
            data: dataToSend
          }).catch();
          if (data.status === 200) {
            toast.success(data.message);
            push('/');
          } else {
            toast.error(data.error);
          }
        })
        .catch((err) => console.error(err));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCal = (name) => (e) => {
    e.stopPropagation();
    setOpenModal(urlMapping[name]);
  };

  return (
    <>
      {isReady && (
        <Script
          id={'gcp-recaptcha'}
          strategy="afterInteractive"
          src={`https://www.google.com/recaptcha/enterprise.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_CLIENT_KEY}`}
        />
      )}
      <SEO title={t('contactus:seoTitle')} allowRobots>
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}/contactus`}
          hrefLang="en"
        />
        <link
          rel="alternate"
          hrefLang="es"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}/es/contactus`}
        />
        <link
          rel="alternate"
          hrefLang="fr"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}/fr/contactus`}
        />
        <link
          rel="alternate"
          hrefLang="en"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}/en/contactus`}
        />
      </SEO>
      <HomeLayout>
        <Container
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            paddingInline: '2rem',
            marginTop: '8rem'
          }}
          maxWidth={'xl'}
        >
          <Typography
            variant={'h4'}
            component={'h1'}
            align={'center'}
            sx={{ width: '100%' }}
          >
            {t('contactus:title')}
          </Typography>
          <Typography
            variant={'body2'}
            component={'p'}
            align={'center'}
            sx={{ width: '100%' }}
          >
            {t('contactus:mandatory')}
          </Typography>
          <Box
            component={'form'}
            onSubmit={handleSubmit(handleContactForm)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Box
              sx={{
                maxWidth: '600px',
                gap: '1.5rem',
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column'
              }}
            >
              {INPUTS.filter(({ type }) => type === 'text').map(({ name }) => {
                return (
                  <TextField
                    key={name}
                    fullWidth
                    label={t(`contactus:${name}`)}
                    variant="standard"
                    {...register(name, { required: true })}
                    // @ts-expect-error
                    error={errors[name]}
                  />
                );
              })}
              <FormGroup>
                {INPUTS.filter(
                  ({ type, notMap }) => type === 'checkbox' && !notMap
                ).map(({ name }) => {
                  return (
                    <Box
                      key={name}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <FormControlLabel
                        control={<Checkbox color="secondary" />}
                        label={t(`contactus:${name}`)}
                        {...register(name, { required: false })}
                      />
                      <Tooltip
                        placement="top"
                        title={`Book a Calendly with Smat's ${name} specialist`}
                      >
                        <Chip
                          color={'primary'}
                          deleteIcon={<CalendarMonthIcon />}
                          onClick={handleOpenCal(name)}
                          onDelete={handleOpenCal(name)}
                          size={'small'}
                          label={t('contactus:meeting')}
                          clickable
                          sx={{
                            boxShadow: 2,
                            '&:hover': { bgcolor: 'purple.main' }
                          }}
                        />
                      </Tooltip>
                    </Box>
                  );
                })}
              </FormGroup>
              <TextField
                label={t('contactus:message')}
                multiline
                rows={4}
                variant="filled"
                {...register(INPUTS_NAME.Message, { required: true })}
                // @ts-expect-error
                error={errors[INPUTS_NAME.Message]}
              />
              <Box>
                <Typography>{t('contactus:disclaimer')}</Typography>
                <FormGroup>
                  <FormControlLabel
                    {...register(INPUTS_NAME.Terms, { required: true })}
                    control={<Checkbox color={'secondary'} />}
                    label={t('common:confirm')}
                  />
                  <FormHelperText
                    sx={{
                      color: 'red',
                      visibility: 'hidden',
                      ...(errors[INPUTS_NAME.Terms] && {
                        visibility: 'initial'
                      })
                    }}
                  >
                    <Box>{t('contactus:check')}</Box>
                  </FormHelperText>
                </FormGroup>
              </Box>
              <Button
                type={'submit'}
                variant={'contained'}
                disabled={loading}
                sx={{
                  ...MAIN_BUTTON,
                  width: '100% !important',
                  maxWidth: 'unset'
                }}
              >
                {loading ? (
                  <CircularProgress
                    sx={{ p: 1, color: loading ? 'primary.main' : 'white' }}
                  />
                ) : (
                  t('common:send')
                )}
              </Button>
            </Box>
          </Box>
        </Container>
        <Modal
          open={!!openModal}
          onClose={() => setOpenModal('')}
          aria-labelledby="calendly-modal"
          aria-describedby="calendly-modal-book-a-meeting"
          onBackdropClick={() => setOpenModal('')}
        >
          <>
            <Box
              sx={{
                bgcolor: 'transparent',
                display: 'flex',
                paddingInline: '1rem',
                ...(isTablet
                  ? { position: 'absolute', left: '10px', top: '10px' }
                  : { position: 'absolute', right: '10px', top: '10px' }),
                justifyContent: 'flex-end'
              }}
            >
              <IconButton
                sx={{
                  shadow: 2,
                  bgcolor: 'common.white',
                  '&:hover': { bgcolor: 'common.lightGrey' }
                }}
                onClick={() => setOpenModal('')}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            {openModal && <Calendly url={openModal} />}
          </>
        </Modal>
      </HomeLayout>
    </>
  );
}

enum INPUTS_NAME {
  Name = 'name',
  Email = 'email',
  Phone = 'phone',
  Country = 'country',
  Token = 'token',
  Invest = 'investing',
  Listing = 'listing',
  // Financing = 'financing',
  Message = 'message',
  Terms = 'terms'
}

const INPUTS = [
  { type: 'text', name: INPUTS_NAME.Name },
  { type: 'text', name: INPUTS_NAME.Email },
  { type: 'text', name: INPUTS_NAME.Phone },
  { type: 'text', name: INPUTS_NAME.Country },
  { type: 'checkbox', name: INPUTS_NAME.Token },
  { type: 'checkbox', name: INPUTS_NAME.Invest },
  { type: 'checkbox', name: INPUTS_NAME.Listing },
  // { type: 'checkbox', name: INPUTS_NAME.Financing },
  { type: 'textarea', name: INPUTS_NAME.Message },
  { type: 'checkbox', name: INPUTS_NAME.Terms, notMap: true }
];

interface FormValues {
  name: INPUTS_NAME.Name;
  email: INPUTS_NAME.Email;
  phone: INPUTS_NAME.Phone;
  country: INPUTS_NAME.Country;
  message: INPUTS_NAME.Message;
  token?: INPUTS_NAME.Token;
  investing?: INPUTS_NAME.Invest;
  listing?: INPUTS_NAME.Listing;
  // financing?: INPUTS_NAME.Financing;
  terms: INPUTS_NAME.Terms;
}

const urlMapping = {
  [INPUTS_NAME.Token]: 'https://calendly.com/smat/discovery-call',
  [INPUTS_NAME.Invest]: 'https://calendly.com/at-smat',
  [INPUTS_NAME.Listing]: 'https://calendly.com/smat/discovery-call'
};

ContactUs.forceLightMode = true;

export default ContactUs;
